// mediaModeration.js — the ONE moderation decision point every image that becomes
// a fill / wallpaper / portrait / image-block routes through, whatever its source.
//
// Why this file exists (LIB-image-library): uploads already pass through a
// moderation call (utils/fileUpload.handleFileUpload -> postMediaModeration,
// POST /api/v2/wheel/image-mod/). Stock/curated library images (the deck's
// "Preset images", proceduralLibrary/presets/manifest) had NO gate — a user could
// select one and it went straight onto the wheel. This wrapper unifies both paths
// so the approve/deny decision for BOTH is made in ONE place.
//
// HONEST framing of what this gate does TODAY (NEM F2):
//   - It returns an explicit `{ approved }` verdict callers can act on — the STOCK
//     picker HONORS it (an un-approved stock selection is refused before onSelect).
//   - The UPLOAD path is intentionally FAIL-OPEN and ADVISORY: a moderation error
//     never blocks the upload (the async backend review flags/removes offending
//     content out of band). This preserves the pre-existing fileUpload.js behaviour
//     on a load-bearing path — it is NOT a hard pre-publish block, and the header
//     claim is scoped to "routed through one decision point," not "cannot publish."
//   - Because both curated stock (pre-vetted) and a successful upload check return
//     `approved: true`, the gate is permissive today. Its value is the single seam:
//     when a real deny policy (hash-allowlist, sync check) is added here, BOTH paths
//     inherit it and the callers that already honor `approved` block for free.
//
// Policy per source:
//   - "upload"  -> user-supplied bytes: run the real server moderation check;
//                  fail-open on error (advisory, matches legacy fileUpload.js).
//   - "stock"   -> app-curated, pre-vetted at curation time (they ship in the
//                  repo/CDN): approved WITHOUT a redundant network round-trip. Still
//                  the single place that decision is made, so an added check applies
//                  to both paths.

import { postMediaModeration } from "../services/api";

export const MODERATION_SOURCE = Object.freeze({
  UPLOAD: "upload",
  STOCK: "stock",
});

// Route one image URL through moderation. Returns a small result descriptor
// (never throws) so callers can proceed deterministically.
//   { moderated: boolean, approved: boolean, source, image_url, skipped, error }
export async function routeImageThroughModeration({
  image_url,
  source = MODERATION_SOURCE.UPLOAD,
  wheel_id = null,
  segment_id = null,
} = {}) {
  if (!image_url) {
    return { moderated: false, approved: false, source, image_url, skipped: true };
  }

  // Curated stock: pre-moderated at curation time — no redundant server call.
  if (source === MODERATION_SOURCE.STOCK) {
    return {
      moderated: true,
      approved: true,
      source,
      image_url,
      skipped: true, // skipped the *network* call, not the gate
    };
  }

  // Uploaded / user-supplied: run the real server-side moderation check.
  try {
    await postMediaModeration({ image_url, wheel_id, segment_id });
    return { moderated: true, approved: true, source, image_url, skipped: false };
  } catch (error) {
    // Match legacy fileUpload behaviour: log, do not block the media.
    console.error("Media moderation failed:", error);
    return { moderated: false, approved: true, source, image_url, skipped: false, error };
  }
}

export default routeImageThroughModeration;
